import React, { useEffect, useState } from 'react';
import styled, { keyframes } from 'styled-components';
import { supabase } from '../../utils/supabase';

const fadeIn = keyframes`
  from { opacity: 0; transform: translateY(10px); }
  to { opacity: 1; transform: translateY(0); }
`;

const Container = styled.div`
  width: 100%;
  max-width: 800px;
  margin: 0 auto;
  padding: 20px;
`;

const Title = styled.h3`
  font-family: 'Plus Jakarta Sans', sans-serif;
  font-size: 1.2rem;
  font-weight: 600;
  color: #ffffff;
  margin-bottom: 20px;
  text-align: center;
  text-transform: uppercase;
  letter-spacing: 1px;
  
  span {
    color: var(--color-neon-green, #0aff0a);
    text-shadow: 0 0 10px rgba(10, 255, 10, 0.4);
  }
`;

const DeploymentList = styled.div`
  display: flex;
  flex-direction: column;
  gap: 12px;
`;

const DeploymentItem = styled.a`
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 16px;
  background: rgba(20, 20, 30, 0.6);
  border: 1px solid rgba(255, 255, 255, 0.05);
  border-radius: 12px;
  text-decoration: none;
  transition: all 0.3s ease;
  animation: ${fadeIn} 0.5s ease-out forwards;
  backdrop-filter: blur(5px);

  &:hover {
    background: rgba(20, 20, 30, 0.8);
    border-color: var(--color-neon-purple, #bc13fe);
    transform: translateX(5px);
    box-shadow: 0 0 20px rgba(188, 19, 254, 0.1);
  }
`;

const TokenInfo = styled.div`
  display: flex;
  align-items: center;
  gap: 12px;
`;

const TokenIcon = styled.img`
  width: 32px;
  height: 32px;
  border-radius: 50%;
  object-fit: cover;
  background: rgba(255, 255, 255, 0.1);
`;

const TokenName = styled.div`
  font-family: 'Plus Jakarta Sans', sans-serif;
  font-weight: 600;
  color: #ffffff;
  font-size: 1rem;
`;

const TokenSymbol = styled.span`
  color: rgba(255, 255, 255, 0.5);
  font-size: 0.85rem;
  margin-left: 8px;
  font-weight: 400;
`;

const TimeAgo = styled.div`
  font-family: 'Plus Jakarta Sans', sans-serif;
  font-size: 0.85rem;
  color: var(--color-neon-green, #0aff0a);
  display: flex;
  align-items: center;
  gap: 6px;

  &::before {
    content: '';
    width: 6px;
    height: 6px;
    background: var(--color-neon-green, #0aff0a);
    border-radius: 50%;
    box-shadow: 0 0 5px var(--color-neon-green, #0aff0a);
  }
`;

interface Token {
    id: string;
    name: string;
    symbol: string;
    logo_url: string;
    created_at: string;
    contract_address: string;
}

export const RecentDeployments = () => {
    const [deployments, setDeployments] = useState<Token[]>([]);

    const fetchDeployments = async () => {
        try {
            const { data, error } = await supabase
                .from('tokens')
                .select('*')
                .eq('network', 'ink')
                .order('created_at', { ascending: false })
                .limit(5);

            if (data) {
                setDeployments(data);
            }
        } catch (err) {
            console.error('Error fetching deployments:', err);
        }
    };

    useEffect(() => {
        fetchDeployments();
        const interval = setInterval(fetchDeployments, 30000); // Refresh every 30s
        return () => clearInterval(interval);
    }, []);

    const getTimeAgo = (dateString: string) => {
        const date = new Date(dateString);
        const now = new Date();
        const seconds = Math.floor((now.getTime() - date.getTime()) / 1000);

        if (seconds < 60) return 'Just now';
        if (seconds < 3600) return `${Math.floor(seconds / 60)}m ago`;
        if (seconds < 86400) return `${Math.floor(seconds / 3600)}h ago`;
        return `${Math.floor(seconds / 86400)}d ago`;
    };

    return (
        <Container>
            <Title>Live <span>Deployments</span></Title>
            <DeploymentList>
                {deployments.map((token, index) => (
                    <DeploymentItem
                        key={token.id}
                        href={`/token/${token.contract_address}`}
                        style={{ animationDelay: `${index * 0.1}s` }}
                    >
                        <TokenInfo>
                            <TokenIcon src={token.logo_url || '/landing-assets/default_token.png'} alt={token.name}
                                onError={(e) => {
                                    (e.target as HTMLImageElement).src = 'https://raw.githubusercontent.com/feathericons/feather/master/icons/circle.svg';
                                }}
                            />
                            <div>
                                <TokenName>
                                    {token.name}
                                    <TokenSymbol>${token.symbol}</TokenSymbol>
                                </TokenName>
                            </div>
                        </TokenInfo>
                        <TimeAgo>{getTimeAgo(token.created_at)}</TimeAgo>
                    </DeploymentItem>
                ))}
            </DeploymentList>
        </Container>
    );
};
